import React from 'react';
import {
  getIframeAnchorTags,
  getUrlParams,
  newRelicClickMetricHandler,
  triggerNewRelic,
  triggerNewRelicTimeLog
} from './helpers';
import { get } from './axios';
import { htmlAttributes, newRelicConstants } from '../constants';
import { initImpressionWatchers } from './initImpressionWatchers';

const revJetBannerConfig = {
  skyscraper: {
    tag: 'tag170885',
    key: 'aec'
  },
  middle_desktop: {
    tag: 'tag179445',
    key: '6ec'
  },
  middle_mobile: {
    tag: 'tag179446',
    key: 'd0e'
  },
  desktop: {
    tag: 'tag98547',
    key: '82a'
  },
  mobile: {
    tag: 'tag98548',
    key: 'dce'
  },
  hero_desktop: {
    tag: 'tag138278',
    key: 'cd8'
  },
  hero_mobile: {
    tag: 'tag154612', key: '82e'
  }
};

const bannersWithMigratedImpressionLogic = ['skyscraper', 'hero'];

function getRevJetConfig(requestConfig) {
  switch (requestConfig.bannerType) {
  case 'skyscraper':
    return revJetBannerConfig.skyscraper;
  case 'middle':
    if (requestConfig.experienceContext.channel === 'desktop') {
      return revJetBannerConfig.middle_desktop;
    }
    return revJetBannerConfig.middle_mobile;
  case 'hero':
    if (requestConfig.experienceContext.channel === 'desktop') {
      return revJetBannerConfig.hero_desktop;
    }
    return revJetBannerConfig.hero_mobile;
  default:
    return revJetBannerConfig[requestConfig.experienceContext.channel];
  }
}

function getPubRef(data) {
  const params = getUrlParams(data.banner.bannerURL);
  return decodeURIComponent(params.pub_ref);
}

function getCreativeId(bannerURL) {
  let creativeId = '';

  if (bannerURL && bannerURL.indexOf('cdn.revjet.com') === -1) {
    const anchorElement = document.createElement('a');
    anchorElement.href = bannerURL;
    creativeId = anchorElement.pathname.split('-')[1];
  }

  return creativeId;
}

function getItcParams({ banner = {} }) {
  return {
    campaignId: banner.campaignId ? banner.campaignId : '',
    placementId: banner.placementId ? banner.placementId : '',
    slotId: banner.slotId ? banner.slotId : '',
    creativeId: getCreativeId(banner.bannerURL)
  };
}

function getTag({ channelConfig, data }) {
  const params = getUrlParams(window.location.href);
  let tag = '';

  if (params.mockSponsoredBannerId) {
    tag = channelConfig.tag + '-' + params.mockSponsoredBannerId;
  } else if (params.useSponsoredMock) {
    tag = channelConfig.tag + '-' + getCreativeId(data.banner.bannerURL);
  } else {
    tag = channelConfig.tag;
  }

  return tag;
}

function getFrameConfig({ data, requestConfig }) {
  const channelConfig = getRevJetConfig(requestConfig);
  const itcParams = getItcParams(data);

  return {
    thd_itc_suffix: true,
    _tag: getTag({ channelConfig, data, requestConfig }),
    _key: channelConfig.key,
    ct_url: data.banner.onClickBeacon,
    piq_slot_id: itcParams.slotId,
    piq_campaign_id: itcParams.campaignId,
    piq_placement_id: itcParams.placementId,
    _opts: {
      embd_tag_id: requestConfig.schemaId,
      autoscale: true,
      b64: getPubRef(data)
    }
  };
}

function isNormalBanner(requestConfig) {
  return requestConfig.bannerType === undefined;
}

function isSkyscraperBanner(requestConfig) {
  return requestConfig.bannerType === 'skyscraper';
}

function isBannerWithMigratedImpressionLogic(requestConfig) {
  return bannersWithMigratedImpressionLogic.includes(requestConfig.bannerType)
    || requestConfig.bannerType === undefined;
}

function frameLoad({ frameConfig, data, requestConfig }) {
  const bannerDomElement = window.document.getElementById(frameConfig._opts.embd_tag_id);

  if (bannerDomElement) {
    if (isNormalBanner(requestConfig)) {
      try {
        const carousel = bannerDomElement.closest('.carousel');
        const next = carousel.querySelector('.carousel__next');
        next.click();
      } catch (error) { /* */ }
    }

    const itcParams = getItcParams(data);
    // Remove from previous banner
    bannerDomElement.removeAttribute('data-hl-beacon');
    bannerDomElement.setAttribute(htmlAttributes.DATA_CAMPAIGN_ID, itcParams.campaignId);
    bannerDomElement.setAttribute(htmlAttributes.DATA_PLACEMENT_ID, itcParams.placementId);
    bannerDomElement.setAttribute(htmlAttributes.DATA_SLOT_ID, itcParams.slotId);
    bannerDomElement.setAttribute(htmlAttributes.DATA_CREATIVE_ID, itcParams.creativeId);
    bannerDomElement.setAttribute('data-usenewimpressionlogic', true);

    let viewBeacon = data.banner.onViewBeacon;
    viewBeacon = viewBeacon ? viewBeacon.replace('https:', '') : '';

    if (isSkyscraperBanner(requestConfig)) {
      triggerNewRelic(newRelicConstants.SKYSCRAPER_BANNER, newRelicConstants.LOAD);
    }

    if (isBannerWithMigratedImpressionLogic(requestConfig)) {
      bannerDomElement.setAttribute('onview-beacon-nucleus', viewBeacon);
    } else {
      bannerDomElement.setAttribute('onview-beacon', viewBeacon);
    }

    // Wait until the iframe content is loaded to add the listeners
    setTimeout(() => {
      const skyscraperAnchorTags = getIframeAnchorTags('skyscraper');
      newRelicClickMetricHandler('skyscraper', skyscraperAnchorTags);
    }, 500);

    if (isNormalBanner(requestConfig)) {
      window.scrollTo(window.scrollX, window.scrollY - 1);
      window.scrollTo(window.scrollX, window.scrollY + 1);
      triggerNewRelicTimeLog(newRelicConstants.STANDARD_BANNER_AD_LOAD, requestConfig.startTime, Date.now());

      if (window.newrelic && !window.__SponsoredBannerLoaded__) {
        window.newrelic.addPageAction('SponsoredBannerLoaded');
        // Dont fire more than once
        window.__SponsoredBannerLoaded__ = true;
      }
      triggerNewRelic(newRelicConstants.STANDARD_BANNER, newRelicConstants.LOAD);
    }
  }
}

function renderFrame({ data, requestConfig }) {
  if (data) {
    const frameConfig = getFrameConfig({ data, requestConfig });
    frameConfig._opts.onload = () => frameLoad({ frameConfig, data, requestConfig });
    window.REVJET_Tag(frameConfig);
  }
}

function injectRevJetScript({ data, requestConfig }) {
  const revJetScript = document.createElement('script');
  revJetScript.onload = () => renderFrame({ data, requestConfig });
  revJetScript.async = true;
  revJetScript.src = 'https://ads.revjet.com/bg';

  const firstScript = document.getElementsByTagName('body')[0];
  firstScript.parentNode.insertBefore(revJetScript, firstScript);
}

function getMockResponse(requestConfig) {
  const { mockSponsoredBannerId } = getUrlParams(window.location.href);
  let revJetConfig = getRevJetConfig(requestConfig);
  let mockRevJetUrl = `http://ads.revjet.com/${revJetConfig.tag}-${mockSponsoredBannerId}`;
  mockRevJetUrl += `?adkey=${revJetConfig.key}&pub_ref=mock`;

  return {
    banner: {
      bannerURL: mockRevJetUrl,
      clickthruURL: 'https://www.homedepot.com/',
      /* eslint-disable-next-line */
      onViewBeacon: 'https://ad-thd-east.tagdelivery.com/impression?id=58c1e65f-1347-4a8e-974e-d1a38c2ff351&x=PQBSrMU8RPJZkBbOrjDSc18Q1ayQXTSmIeTxihTSx1y9TAuGAWAYT6d0LVZ7PodtSlSjEGL3wuSCkAhce75zYtd-2sqdeq-ApKqECLNtI40rBpnEOi566Og3XfUuZsHu0-15T3v3fSc-a7BXv68-EExUCyJFjDF7FlBAnzs1ztKoQWtu2VHTbbD6FKqRRGYRFSG7urhW1pbYdsJ-XPIdtg-L9I-O_rEEcfuW_cPu2-r7wbll5brpZCzuLnnSX5ssi-JC7-an2ySXhP26YTjmLySo4v5i7-KmTyDrmhWLWShNRqc3V43yMUQszMuiaffUgej1g-fhSG6wGdKRbQNexLFce3CmTg_wurbZat-PyhTk-i2D4AJc4qXbxoJvQO3LoIkN0kIMiQMFfuv2jn_7h3-GyF7knjxDEYzBAf6rNtc%3D',
      /* eslint-disable-next-line */
      onClickBeacon: 'https://ad-thd-east.tagdelivery.com/click?id=58c1e65f-1347-4a8e-974e-d1a38c2ff351&x=PQBSrMU8RPJZkBbOrjDSc18Q1ayQXTSmIeTxihTSx1y9TAuGAWAYT6d0LVZ7PodtSlSjEGL3wuSCkAhce75zYtd-2sqdeq-ApKqECLNtI40rBpnEOi566Og3XfUuZsHu0-15T3v3fSc-a7BXv68-EExUCyJFjDF7FlBAnzs1ztKoQWtu2VHTbbD6FKqRRGYRFSG7urhW1pbYdsJ-XPIdtg-L9I-O_rEEcfuW_cPu2-r7wbll5brpZCzuLnnSX5ssi-JC7-an2ySXhP26YTjmLySo4v5i7-KmTyDrmhWLWShNRqc3V43yMUQszMuiaffUgej1g-fhSG6wGdKRbQNexLFce3CmTg_wurbZat-PyhTk-i2D4AJc4qXbxoJvQO3LoIkN0kIMiQMFfuv2jn_7h3-GyF7knjxDEYzBAf6rNtc%3D&redirect=',
      slotId: '1123',
      campaignId: 'abc123',
      placementId: 'xyz123'
    }
  };
}

function buildRevJetBanner({ data, requestConfig }) {
  if (window.REVJET_Tag) {
    renderFrame({ data, requestConfig });
  } else {
    injectRevJetScript({ data, requestConfig });
  }
}

function buildRevJetBannerMock(requestConfig) {
  const data = getMockResponse(requestConfig);

  buildRevJetBanner({ data, requestConfig });
}

function getHeroBannerPosition() {
  const defaultPosition = 2;
  const value = typeof window !== 'undefined' ? window.cookieUtils?.readBrowserCookie('xtt-tntHeroBannerPos') : null;
  return value ? value - 1 : defaultPosition;
}

async function getSponsoredBanner({
  newCarousel, channel, cx, classNames, isGuest, experienceContext
}) {

  const bannerChannel = channel === 'desktop' ? 'desktop' : 'mobileweb';
  const browserID = typeof window !== 'undefined' ? experienceContext?.cookie?.adobeCookie?.MCMID : null;
  const bannerSchema = isGuest ? 'hp_sponsored_auth' : 'hp_sponsored';
  const heroBannerPosition = getHeroBannerPosition();
  const heroRequestConfig = {
    schema: bannerSchema,
    experienceContext: {
      channel,
    },
    carouselType: null,
    bannerType: 'hero',
    schemaId: 'hp_sponsored__hero',
  };
  await get({
    params: {
      version: 'model2',
      key: '12345678912345678912345678900000',
      appid: bannerChannel,
      customerType: 'b2c',
      browserid: browserID,
      schema: bannerSchema,
      show: 'banner',
      bannerType: 'hero',
      bannerPosition: heroBannerPosition,
    }
  }).then((response) => {
    if (response.status === 200 && response?.data) {
      const { data } = response;
      initImpressionWatchers();
      let tempChild = (
        <div
          id="hp_sponsored__hero"
          key={`item-${newCarousel.length}`}
          className={cx(classNames)}
          data-campaignid={data?.banner?.campaignId}
          data-placementid={data?.banner?.placementId}
          data-slotid={data?.banner?.slotId}
          data-creativeid={data?.banner?.creativeId}
          data-onview-beacon-nucleus={data?.banner?.onViewBeacon}
        >
          {newCarousel[0]}
        </div>
      );
      newCarousel.splice(heroBannerPosition, 0, tempChild);
      injectRevJetScript({ data, requestConfig: heroRequestConfig });
    }
  }).catch((error) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('error-boundary.error', {
        error,
        id: newRelicConstants.HERO_BANNER
      });
    }
  });
  return newCarousel;
}

export {
  buildRevJetBanner, buildRevJetBannerMock, getFrameConfig, frameLoad, getSponsoredBanner
};
